/**
 * useAccountSelection Hook
 * Cari hesap seçimi işlemlerini yönetir
 */

import { useState, useCallback } from 'react';
import { Account } from '../components/types/invoice.types';
import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API, handleApiError } from '../../../../config/api';

export const useAccountSelection = () => {
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsRowsPerPage, setAccountsRowsPerPage] = useState(10);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  /**
   * Cari hesapları yükle
   */
  const loadAccounts = useCallback(async (companyId: string) => {
    try {
      setLoadingAccounts(true);
      setAccountsError(null);
      console.log('📋 [CARİ] Cari hesaplar yükleniyor...', { companyId });

      const { data: result } = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const accountsData = result?.data ?? result ?? [];

      console.log('✅ [CARİ] Cari hesaplar yüklendi:', accountsData.length);
      setAccounts(accountsData);
    } catch (error) {
      console.error('❌ [CARİ] Cari hesaplar yüklenirken hata:', error);
      setAccountsError(handleApiError(error));
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  /**
   * Cari hesap seç
   */
  const handleSelectAccount = useCallback((account: Account) => {
    console.log('✅ [CARİ] Cari seçildi:', account.companyName);
    setSelectedAccount(account);
    setAccountModalOpen(false);
    setAccountSearch('');
  }, []);

  /**
   * Cari modalını aç
   */
  const handleOpenAccountModal = useCallback(() => {
    setAccountModalOpen(true);
  }, []);

  /**
   * Cari modalını kapat
   */
  const handleCloseAccountModal = useCallback(() => {
    setAccountModalOpen(false);
    setAccountSearch('');
  }, []);

  /**
   * Cari arama
   */
  const handleAccountSearch = useCallback((value: string) => {
    setAccountSearch(value);
    setAccountsPage(0); // Aramada ilk sayfaya dön
  }, []);

  /**
   * Sayfalama - Sayfa değişimi
   */
  const handleAccountsPageChange = useCallback((
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setAccountsPage(newPage);
  }, []);

  /**
   * Sayfalama - Sayfa başına satır değişimi
   */
  const handleAccountsRowsPerPageChange = useCallback((
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setAccountsRowsPerPage(parseInt(event.target.value, 10));
    setAccountsPage(0);
  }, []);

  /**
   * Filtrelenmiş hesaplar (arama ile)
   */
  const filteredAccounts = accounts.filter((account) => {
    if (!accountSearch) return true;
    
    const search = accountSearch.toLowerCase();
    return (
      account.companyName.toLowerCase().includes(search) ||
      account.taxNumber?.toLowerCase().includes(search) ||
      account.phone?.toLowerCase().includes(search) ||
      account.email?.toLowerCase().includes(search)
    );
  });

  /**
   * Sayfalanmış hesaplar
   */
  const paginatedAccounts = filteredAccounts.slice(
    accountsPage * accountsRowsPerPage,
    accountsPage * accountsRowsPerPage + accountsRowsPerPage
  );

  return {
    // State
    selectedAccount,
    setSelectedAccount,
    accounts,
    accountModalOpen,
    accountSearch,
    accountsPage,
    accountsRowsPerPage,
    loadingAccounts,
    accountsError,

    // Filtered/Paginated Data
    filteredAccounts,
    paginatedAccounts,

    // Actions
    loadAccounts,
    handleSelectAccount,
    handleOpenAccountModal,
    handleCloseAccountModal,
    handleAccountSearch,
    handleAccountsPageChange,
    handleAccountsRowsPerPageChange,
  };
};

